import { Injectable, signal } from '@angular/core';
import { FiscalPrinterForm, FiscalPrinterSettings, GeneralSettingsFields, GeneralSettingsForm, GeneralSettingsFormGroups, GeneralSettingsValidators, NoFiscalPrinterForm } from '../models/settings-printers.model';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Validators } from '@angular/forms';

@Injectable()
export class GeneralSettingsFormService {

  private generalSettingsForm!: GeneralSettingsFormGroups;
  
  get form() {
    return this.generalSettingsForm;
  }

  generalSettingsFormReady = signal(false);

  validators: GeneralSettingsValidators = {
    displayMessageMaxLength: 36,
    sellerPrefixMaxLength: 25,
    tailMaxLength: 255,
    qrCodeMessageMaxLength: 255,
    qrCodeUrlMaxLength: 255,
    headerRowMaxLength: 40,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  createForm(settings: GeneralSettingsFields | null) {
    this.generalSettingsFormReady.set(false);
    
    const fiscalGroup = new CustomFormGroup<FiscalPrinterForm>({
      disable_header_configuration: new CustomFormControl(
        settings?.fiscalprinter?.disable_header_configuration ?? false
      ),
      ticket_change: new CustomFormControl(
        settings?.fiscalprinter?.ticket_change ?? false
      ),
      print_notes: new CustomFormControl(
        settings?.fiscalprinter?.print_notes ?? false
      ),
      print_description: new CustomFormControl(
        settings?.fiscalprinter?.print_description ?? false
      ),
      print_name: new CustomFormControl(
        settings?.fiscalprinter?.print_name ?? false
      ),
      print_barcode_id: new CustomFormControl(
        settings?.fiscalprinter?.print_barcode_id ?? false
      ),
      print_sale_qr_code: new CustomFormControl(
        settings?.fiscalprinter?.print_sale_qr_code ?? false
      ),
      print_customer_details: new CustomFormControl(
        settings?.fiscalprinter?.print_customer_details ?? false
      ),
      print_seller: new CustomFormControl(
        settings?.fiscalprinter?.print_seller ?? false
      ),
      print_daily_read_on_closing: new CustomFormControl(
        settings?.fiscalprinter?.print_daily_read_on_closing ?? false
      ),
      display_message: new CustomFormControl(
        settings?.fiscalprinter?.display_message ?? '',
        { validators: [Validators.maxLength(this.validators.displayMessageMaxLength)] }
      ),
      seller_prefix: new CustomFormControl(
        settings?.fiscalprinter?.seller_prefix ?? '',
        { validators: [Validators.maxLength(this.validators.sellerPrefixMaxLength)] }
      ),
      tail: new CustomFormControl(
        settings?.fiscalprinter?.tail ?? '',
        { validators: [Validators.maxLength(this.validators.tailMaxLength)] }
      ),
      qr_code_message: new CustomFormControl(
        settings?.fiscalprinter?.qr_code_message ?? '',
        { validators: [Validators.maxLength(this.validators.qrCodeMessageMaxLength)] }
      ),
      qr_code_url: new CustomFormControl(
        settings?.fiscalprinter?.qr_code_url ?? '',
        { validators: [Validators.maxLength(this.validators.qrCodeUrlMaxLength)] }
      ),

      ...this.createDynamicControls(settings?.fiscalprinter)
    } as FiscalPrinterForm)

    const noFiscalGroup = new CustomFormGroup<NoFiscalPrinterForm>({
      goExit_skip_content: new CustomFormControl(
        settings?.nofiscalprinter?.goExit_skip_content ?? false
      ),
      print_included_ingredients: new CustomFormControl(
        settings?.nofiscalprinter?.print_included_ingredients ?? false
      )
    } as NoFiscalPrinterForm);

    this.generalSettingsForm = new CustomFormGroup<GeneralSettingsForm>({
      fiscalprinter: fiscalGroup,
      nofiscalprinter: noFiscalGroup
    });

    this.generalSettingsFormReady.set(true);
    return this.generalSettingsForm;
  }

  private createDynamicControls(fiscalData: FiscalPrinterSettings | undefined) {
    const dynamicControls: any = {};
    
    for (let i = 1; i <= 13; i++) {
      const key = `printer_header_${i}`;
      dynamicControls[key] = new CustomFormControl(fiscalData?.[key as keyof FiscalPrinterSettings] ?? '');
    }
    
    // for (let i = 1; i <= 6; i++) {
    //   const key = `printer_invoice_footer_${i}`;
    //   dynamicControls[key] = new CustomFormControl(fiscalData?.[key as keyof FiscalPrinterSettings] ?? '');
    // }
    
    return dynamicControls;
  }

  get fiscalGroup(): CustomFormGroup<FiscalPrinterForm> {
    return this.generalSettingsForm.controls.fiscalprinter;
  }

  get noFiscalGroup(): CustomFormGroup<NoFiscalPrinterForm> {
    return this.generalSettingsForm.controls.nofiscalprinter;
  }

  get disableHeaderConfig() {
    return this.fiscalGroup.controls.disable_header_configuration;
  }

  get ticket_change() {
    return this.fiscalGroup.controls.ticket_change;
  }
  
  get print_notes() {
    return this.fiscalGroup.controls.print_notes;
  }
  
  get print_description() {
    return this.fiscalGroup.controls.print_description;
  }
  
  get print_name() {
    return this.fiscalGroup.controls.print_name;
  }
  
  get print_barcode_id() {
    return this.fiscalGroup.controls.print_barcode_id;
  }
  
  get print_sale_qr_code() {
    return this.fiscalGroup.controls.print_sale_qr_code;
  }
  
  get print_customer_details() {
    return this.fiscalGroup.controls.print_customer_details;
  }
  
  get print_seller() {
    return this.fiscalGroup.controls.print_seller;
  }
  
  get print_daily_read_on_closing() {
    return this.fiscalGroup.controls.print_daily_read_on_closing;
  }
  
  get display_message() {
    return this.fiscalGroup.controls.display_message;
  }
  
  get seller_prefix() {
    return this.fiscalGroup.controls.seller_prefix;
  }
  
  get tail() {
    return this.fiscalGroup.controls.tail;
  }
  
  get qr_code_message() {
    return this.fiscalGroup.controls.qr_code_message;
  }
  
  get qr_code_url() {
    return this.fiscalGroup.controls.qr_code_url;
  }

  getPrinterHeader(index: number) {
    return this.fiscalGroup.controls[`printer_header_${index}`];
  }

  get goExit_skip_content() {
    return this.noFiscalGroup.controls.goExit_skip_content;
  }
  
  get print_included_ingredients() {
    return this.noFiscalGroup.controls.print_included_ingredients;
  }
  
}
